import { Component, OnInit, OnDestroy } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

interface Account {
  id: number;
  name: string;
}

interface AppliedFilters {
  accountIds: number[];
  accountNames: string[];
}

@Component({
  selector: 'app-bulk-update-filter-preview',
  templateUrl: './bulk-update-filter-preview.component.html',
  styleUrls: ['./bulk-update-filter-preview.component.scss']
})
export class BulkUpdateFilterPreviewComponent implements OnInit, OnDestroy {
  accounts: Account[] = [];
  selectedAccounts: Account[] = [];
  searchValue: string = '';
  private closedWithPayload = false;

  constructor(
    public dialogRef: DynamicDialogRef,
    private config: DynamicDialogConfig
  ) { }

  ngOnInit(): void {
    const appliedFilters = this.config.data.appliedFilters as AppliedFilters;
    const excludedAccountIds: number[] = this.config.data?.excludedAccountIds || [];
    this.initializeAccounts(appliedFilters);

    if (excludedAccountIds.length > 0) {
      const excludedSet = new Set(excludedAccountIds);
      this.selectedAccounts = this.accounts.filter((account) => !excludedSet.has(account.id));
    } else {
      this.selectedAccounts = [...this.accounts];
    }
  }

  /**
   * Method to initialize the accounts based on the applied filters
   * @param appliedFilters
   */
  initializeAccounts(appliedFilters: AppliedFilters): void {
    if (appliedFilters && appliedFilters.accountIds && appliedFilters.accountNames) {
      this.accounts = appliedFilters.accountIds.map((id, index) => {
        return {
          id: id,
          name: appliedFilters.accountNames[index] || ''
        };
      });
    } else {
      this.accounts = [];
    }
  }

  /**
   * Toggle account selection
   * @param account The account to toggle
   */
  toggleAccountSelection(account: Account): void {
    const index = this.selectedAccounts.findIndex((selectedAccount) => selectedAccount.id === account.id);

    if (index > -1) {
      this.selectedAccounts = this.selectedAccounts.filter((selectedAccount) => selectedAccount.id !== account.id);
    } else {
      this.selectedAccounts = [...this.selectedAccounts, account];
    }
  }

  /**
   * Check if an account is selected
   * @param account The account to check
   * @returns True if the account is selected, false otherwise
   */
  isAccountSelected(account: Account): boolean {
    return this.selectedAccounts.some((selectedAccount) => selectedAccount.id === account.id);
  }

  /**
   * Get the selected accounts
   * @returns Array of selected accounts
   */
  getSelectedAccounts(): Account[] {
    return this.selectedAccounts;
  }

  /**
   * Get the excluded accounts (those not selected)
   */
  getExcludedAccounts(): Account[] {
    const selectedSet = new Set(this.selectedAccounts.map((account) => account.id));
    return this.accounts.filter((account) => !selectedSet.has(account.id));
  }

  /**
   * Clear all exclusions (select all accounts)
   */
  clearSelected(): void {
    this.selectedAccounts = [...this.accounts];
  }

  /**
   * Get excluded account IDs (for closing payload)
   */
  getExcludedAccountIds(): number[] {
    return this.getExcludedAccounts().map(function(account) { return account.id; });
  }

  /**
   * Close dialog and persist excludedAccountIds
   */
  closeWithPayload(): void {
    if (!this.closedWithPayload) {
      this.closedWithPayload = true;
      this.dialogRef.close({ excludedAccountIds: this.getExcludedAccountIds() });
    }
  }

  ngOnDestroy(): void {
    if (!this.closedWithPayload) {
      this.closedWithPayload = true;
        this.dialogRef.close({ excludedAccountIds: this.getExcludedAccountIds() });
    }
  }
}
